import React, { useMemo, useState, useCallback } from 'react';
import StatusActionButton from '../buttons/StatusActionButton';
import styles from './PaymentList.module.css';

export interface PaymentAllocation {
  allocationId: number;
  allocationType?: 'activity' | 'contract' | 'paymentLine' | 'session';
  sessionTimeStart?: string;
  sessionTimeEnd?: string;
  sessionHours?: number;
  allocationAmount: number;
  sessionSiteName?: string;
  sessionActivityId?: number; // ID активности для подчеркивания
  sessionActivityTypeOfWorkName?: string; // Название вида работы активности
  contractTitle?: string;
  contractDescription?: string;
  paymentLineType?: string;
  paymentLineDescription?: string;
  isAllocated?: boolean;
  isCurrentActivity?: boolean; // Флаг для подчеркивания текущей активности
  childAllocations?: PaymentAllocation[];
  isExpanded?: boolean;
}

export interface Payment {
  id: number;
  userId?: number;
  siteExecutorId?: number;
  userName?: string;
  date: string;
  amount: number;
  allocatedAmount?: number;
  remainingAmount?: number;
  signatureAdmin: boolean;
  signatureUser: boolean;
  description?: string;
  allocations?: PaymentAllocation[];
}

export interface PaymentListProps {
  payments: Payment[];
  onPaymentClick?: (payment: Payment) => void;
  onPaymentExpand?: (payment: Payment) => void;
  onAllocateClick?: (payment: Payment) => void;
  formatTime?: (timeString: string) => string;
  formatDate?: (timeString: string) => string;
  formatAmount?: (amount?: number) => string;
  loading?: boolean;
  loadingAllocations?: Record<number, boolean>;
  showAllocateButton?: boolean;
  emptyState?: {
    icon: string;
    title: string;
    subtitle?: string;
  };
}

// Мемоизированный компонент распределения платежа
const AllocationItem = React.memo<{
  allocation: PaymentAllocation;
  formatTime: (timeString: string) => string;
  formatAmount: (amount?: number) => string;
  onToggleExpand?: (allocationId: number) => void;
  getExpanded?: (allocationId: number) => boolean;
}>(({ allocation, formatTime, formatAmount, onToggleExpand, getExpanded }) => {
  const isPaymentLine = allocation.allocationType === 'paymentLine';
  const isSession = allocation.allocationType === 'session';
  const isActivity = allocation.allocationType === 'activity';
  const isContract = allocation.allocationType === 'contract';
  const hasChildren = allocation.childAllocations && allocation.childAllocations.length > 0;
  // Статьи платежа и смены раскрываемы; смена показывает сумму и список активностей
  const isExpandable = isPaymentLine || isSession || hasChildren;
  const isExpanded = getExpanded ? getExpanded(allocation.allocationId) : (allocation.isExpanded ?? false);
  const handleToggle = onToggleExpand ? () => onToggleExpand(allocation.allocationId) : undefined;
  
  let title = '';
  let details: string[] = [];
  
  if (isPaymentLine) {
    title = allocation.paymentLineType || 'Статья платежа';
    if (allocation.paymentLineDescription) {
      details.push(allocation.paymentLineDescription);
    }
  } else if (isSession) {
    // Дата с днём недели, время начала–конца, объект
    const sessionDate = allocation.sessionTimeStart
      ? new Date(allocation.sessionTimeStart).toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          weekday: 'short'
        })
      : '';
    const timeStart = allocation.sessionTimeStart ? formatTime(allocation.sessionTimeStart) : '';
    const timeEnd = allocation.sessionTimeEnd ? formatTime(allocation.sessionTimeEnd) : '';
    title = sessionDate || '—';
    if (timeStart) details.push(timeStart);
    if (timeEnd) details.push(timeEnd);
    if (allocation.sessionSiteName) details.push(allocation.sessionSiteName);
  } else if (isActivity) {
    // Для активностей - название вида работы, дата, время, часы
    title = allocation.sessionActivityTypeOfWorkName || 'Активность';
    const sessionDate = allocation.sessionTimeStart 
      ? new Date(allocation.sessionTimeStart).toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit'
        })
      : '';
    const sessionTime = allocation.sessionTimeStart
      ? formatTime(allocation.sessionTimeStart)
      : '';
    const hours = allocation.sessionHours ? `${allocation.sessionHours.toFixed(1)} ч` : '';
    
    if (sessionDate) details.push(sessionDate);
    if (sessionTime) details.push(sessionTime);
    if (hours) details.push(hours);
  } else if (isContract) {
    title = allocation.contractTitle || 'Контракт';
    if (allocation.contractDescription) {
      details.push(allocation.contractDescription);
    }
  }

  return (
    <>
      <div 
        className={`${styles.allocationItem} ${isExpandable ? styles.expandable : ''} ${allocation.isCurrentActivity ? styles.currentActivity : ''} ${isSession ? styles.sessionRow : ''}`}
        onClick={isExpandable ? handleToggle : undefined}
        title={isExpandable ? (isExpanded ? 'Нажмите, чтобы свернуть' : 'Нажмите, чтобы раскрыть') : undefined}
      >
        <div className={styles.allocationContent}>
          {isExpandable && (
            <span className={styles.expandIcon}>
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          <div>
            <div className={styles.allocationTitle}>
              {title}
            </div>
            {details.length > 0 && (
              <div className={styles.allocationDetails}>
                {details.join(' • ')}
              </div>
            )}
          </div>
        </div>
        <div className={styles.allocationRight}>
          <div className={styles.allocationAmount}>
            {formatAmount(allocation.allocationAmount)}
          </div>
        </div>
      </div>
      
      {/* Вложенные распределения: для статьи платежа — смены/контракты; для смены — активности */}
      {isExpandable && isExpanded && (
        <div className={styles.childAllocations}>
          {hasChildren ? (
            allocation.childAllocations!.map((child) => (
              <AllocationItem
                key={child.allocationId}
                allocation={{ ...child, isExpanded: getExpanded ? getExpanded(child.allocationId) : child.isExpanded }}
                formatTime={formatTime}
                formatAmount={formatAmount}
                onToggleExpand={onToggleExpand}
                getExpanded={getExpanded}
              />
            ))
          ) : isPaymentLine ? (
            <div className={styles.childAllocationsEmpty}>
              <i className="ri-inbox-line"></i>
              <span>За этой статьёй нет активностей или контрактов</span>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
});

AllocationItem.displayName = 'AllocationItem';

// Мемоизированный компонент платежа
const PaymentItem = React.memo<{
  payment: Payment;
  allocations: PaymentAllocation[];
  isLoading: boolean;
  onPaymentClick?: (payment: Payment) => void;
  onPaymentExpand?: (payment: Payment) => void;
  onAllocateClick?: (payment: Payment) => void;
  showAllocateButton?: boolean;
  formatTime: (timeString: string) => string;
  formatDate: (timeString: string) => string;
  formatAmount: (amount?: number) => string;
}>(({ payment, allocations = [], isLoading, onPaymentClick, onPaymentExpand, onAllocateClick, showAllocateButton, formatTime, formatDate, formatAmount }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedAllocations, setExpandedAllocations] = useState<Set<number>>(new Set());

  const toggleExpanded = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    // Если раскрываем и есть callback, вызываем его для загрузки данных
    if (newExpanded && onPaymentExpand) {
      onPaymentExpand(payment);
    }
  }, [isExpanded, onPaymentExpand, payment]);

  const toggleAllocationExpanded = useCallback((allocationId: number) => {
    setExpandedAllocations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(allocationId)) {
        newSet.delete(allocationId);
      } else {
        newSet.add(allocationId);
      }
      return newSet;
    });
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Если есть onPaymentClick, вызываем его и останавливаем всплытие
    if (onPaymentClick) {
      e.stopPropagation();
      onPaymentClick(payment);
    }
    // Если onPaymentClick нет, позволяем событию всплыть к paymentHeader для toggleExpanded
  }, [onPaymentClick, payment]);

  const isSigned = payment.signatureAdmin && payment.signatureUser;
  const statusClass = isSigned ? styles.signed : styles.pending;

  // Проверяем, полностью ли распределен платеж (до копеек; 0,01 ₽ — всё ещё остаток, показываем строку)
  const hasAllocations = payment.allocatedAmount !== undefined && payment.allocatedAmount > 0;
  const remaining = payment.remainingAmount ?? (payment.amount - (payment.allocatedAmount ?? 0));
  const remainderRounded = Math.round(remaining * 100) / 100;
  const isFullyAllocated = remainderRounded <= 0;

  const paymentRightContent = useMemo(() => {
    
    return (
      <div className={styles.paymentRightWrapper}>
        {hasAllocations && (
          <div className={styles.allocationSummarySpacer}></div>
        )}
        <div className={styles.paymentRightCenter}>
          <div className={styles.paymentAmount}>
            {formatAmount(payment.amount)}
          </div>
          <div className={styles.paymentStatus}>
            <div className={styles.expandIcon}>
              <i className={isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
            </div>
          </div>
        </div>
        {hasAllocations && (
          <div className={styles.allocationSummary}>
            <div className={styles.allocatedText}>
              {formatAmount(payment.allocatedAmount)} распределено
            </div>
            {!isFullyAllocated && (
              <div className={styles.remainingText}>
                {formatAmount(remainderRounded)} остаток
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [
    payment.amount,
    payment.allocatedAmount,
    payment.remainingAmount,
    hasAllocations,
    isFullyAllocated,
    isExpanded,
    formatAmount,
    remainderRounded,
  ]);

  return (
    <div className={`${styles.paymentItem} ${statusClass}`} data-expanded={isExpanded}>
      <div 
        className={styles.paymentHeader}
        onClick={toggleExpanded}
        title={isExpanded ? 'Нажмите, чтобы свернуть' : 'Нажмите, чтобы раскрыть'}
      >
        <div className={styles.paymentLeft}>
          <div className={styles.paymentInfo}>
            <div className={styles.paymentUserName}>
              {payment.userName || `Пользователь ${payment.userId}`}
            </div>
          </div>
        </div>
        <div 
          className={styles.paymentRight}
          onClick={handleClick}
        >
          {paymentRightContent}
        </div>
      </div>
      
      <div className={styles.paymentAllocations}>
        {isExpanded && (
          <>
            <div className={styles.paymentDetails}>
              <div className={styles.paymentDetailItem}>
                <i className="ri-calendar-line"></i>
                <span>{formatDate(payment.date)}</span>
              </div>
              <div className={styles.paymentDetailItem}>
                <i className="ri-time-line"></i>
                <span>{formatTime(payment.date)}</span>
              </div>
              <div className={styles.paymentDetailItem}>
                <i className={isSigned ? 'ri-checkbox-circle-line' : 'ri-time-line'}></i>
                <span>{isSigned ? 'Подписан' : 'Ожидает подписи'}</span>
              </div>
            </div>
            {payment.description && (
              <div className={styles.paymentDescriptionBlock}>
                <i className="ri-message-3-line"></i>
                <div className={styles.paymentDescriptionText}>
                  {payment.description}
                </div>
              </div>
            )}
          </>
        )}
        {isLoading && allocations.length === 0 ? (
          <div className={styles.loadingState}>
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка распределений...</span>
          </div>
        ) : allocations.length > 0 ? (
          allocations.map((allocation) => (
            <AllocationItem
              key={allocation.allocationId}
              allocation={{
                ...allocation,
                isExpanded: expandedAllocations.has(allocation.allocationId)
              }}
              formatTime={formatTime}
              formatAmount={formatAmount}
              onToggleExpand={toggleAllocationExpanded}
              getExpanded={(id) => expandedAllocations.has(id)}
            />
          ))
        ) : isExpanded ? (
          <div className={styles.emptyState}>
            <i className="ri-inbox-line"></i>
            <span>Платеж не распределен по сессиям</span>
          </div>
        ) : null}
        {showAllocateButton && onAllocateClick && isExpanded && !isFullyAllocated && (
          <div 
            className={styles.allocateButtonContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <StatusActionButton
              variant="primary"
              fullWidth
              icon="ri-hand-coin-line"
              onClick={() => onAllocateClick(payment)}
            >
              Распределить
            </StatusActionButton>
          </div>
        )}
      </div>
    </div>
  );
});

PaymentItem.displayName = 'PaymentItem';

// Мемоизированный компонент заголовка даты
const DateHeader = React.memo<{
  date: string;
  totalAmount: number;
  formatAmount: (amount?: number) => string;
}>(({ date, totalAmount, formatAmount }) => {
  return (
    <div className={styles.dateHeader}>
      <span className={styles.dateHeaderText}>{date}</span>
      <span className={styles.dateHeaderAmount}>{formatAmount(totalAmount)}</span>
    </div>
  );
});

DateHeader.displayName = 'DateHeader';

// Основной компонент списка платежей
const PaymentList: React.FC<PaymentListProps> = ({
  payments,
  onPaymentClick,
  onPaymentExpand,
  onAllocateClick,
  formatTime,
  formatDate,
  formatAmount,
  loading = false,
  loadingAllocations = {},
  showAllocateButton = false,
  emptyState
}) => {
  // Функции форматирования по умолчанию
  const defaultFormatTime = useCallback((timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const defaultFormatDate = useCallback((timeString: string) => {
    const date = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }, []);

  const defaultFormatAmount = useCallback((amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
  }, []);

  const finalFormatTime = formatTime || defaultFormatTime;
  const finalFormatDate = formatDate || defaultFormatDate;
  const finalFormatAmount = formatAmount || defaultFormatAmount;

  // Группируем платежи по дате
  const groupedPayments = useMemo(() => {
    const groups: { [key: string]: Payment[] } = {};

    payments.forEach(payment => {
      const date = finalFormatDate(payment.date);
      
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(payment);
    });

    return Object.entries(groups).sort((a, b) => {
      const dateA = new Date(a[1][0].date);
      const dateB = new Date(b[1][0].date);
      return dateB.getTime() - dateA.getTime();
    });
  }, [payments, finalFormatDate]);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <i className="ri-loader-4-line ri-spin"></i>
        <span>Загрузка платежей...</span>
      </div>
    );
  }

  if (payments.length === 0) {
    if (emptyState) {
      return (
        <div className={styles.emptyState}>
          <i className={emptyState.icon}></i>
          <span>{emptyState.title}</span>
          {emptyState.subtitle && <small>{emptyState.subtitle}</small>}
        </div>
      );
    }
    return null;
  }

  return (
    <div className={styles.paymentList}>
      {groupedPayments.map(([date, datePayments]) => {
        const totalAmountForDate = datePayments.reduce((sum, p) => sum + (p.amount || 0), 0);
        return (
          <React.Fragment key={date}>
            <DateHeader 
              date={date} 
              totalAmount={totalAmountForDate}
              formatAmount={finalFormatAmount}
            />
            {datePayments.map((payment) => (
              <PaymentItem
                key={payment.id}
                payment={payment}
                allocations={payment.allocations || []}
                isLoading={loadingAllocations[payment.id] || false}
                onPaymentClick={onPaymentClick}
                onPaymentExpand={onPaymentExpand}
                onAllocateClick={onAllocateClick}
                showAllocateButton={showAllocateButton}
                formatTime={finalFormatTime}
                formatDate={finalFormatDate}
                formatAmount={finalFormatAmount}
              />
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default PaymentList;

